/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { xScreen } from "@stoneage/XDL/Utilities";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    pagination: state.xProductInfo.data?.pagination,
    pageTitle: getCurrentPage(state).fields.title,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ProductInformationPaginationStyles {
    wrapper?: InjectableCss;
    infoMessage?: TypographyPresentationProps;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    totalProducts?: TypographyProps;
}

export const paginationStyles: ProductInformationPaginationStyles = {
    infoMessage: {
        color: "text.main",
        size: 12,
        lineHeight: "18px",
        css: css`
            display: block;
            margin-bottom: 6px;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            margin-bottom: 24px;
            ${xScreen("lte", "sm")} {
                flex-wrap: wrap;
            }
        `,
    },
    title: {
        variant: "h1",
        lineHeight: "30px",
        css: css`
            margin-bottom: 0;
            margin-right: 32px;
            ${xScreen("lte", "xs")} {
                margin-right: 0;
                margin-bottom: 12px;
            }
        `,
    },
    totalProducts: {
        size: "18px",
        lineHeight: "21px",
        weight: "bold",
        color: "common.black",
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                display: block;
            }
        `,
    },
};

const styles = paginationStyles;

const ProductInformationPaginationInfo = ({ pagination, pageTitle }: Props) => {
    const { currentPage, pageSize, totalItemCount = 0, numberOfPages } = (pagination as PaginationModel) || {};

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography as="h1" {...styles.title}>
                    {pageTitle}
                </Typography>
                <Typography {...styles.totalProducts}>{translate("{0} Products", `${totalItemCount}`)}</Typography>
            </StyledWrapper>
            {!!totalItemCount && (
                <Typography {...styles.infoMessage}>
                    {translate(
                        "Showing {0}-{1} of {2} Products",
                        `${(currentPage - 1) * pageSize + 1}`,
                        `${currentPage !== numberOfPages ? currentPage * pageSize : totalItemCount}`,
                        `${totalItemCount}`,
                    )}
                </Typography>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductInformationPaginationInfo),
    definition: {
        group: "Product Information" as any,
        displayName: "Pagination Info",
        allowedContexts: ["ProductInformationPage"] as any,
    },
};

export default widgetModule;
